import { useTranslation } from 'react-i18next';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
} from '@/components/ui';
import {
  locumContractErrorKind,
  useMyLocumContract,
  type LocumContract,
} from '@/features/locum/api';
import { PageContainer } from '@/layouts/PageContainer';
import styles from './LocumPages.module.css';
import { clinicDate } from '@/lib/clinic-time';

function dateLabel(value: string) {
  return new Intl.DateTimeFormat('en-MY', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${value}T00:00:00`));
}

function money(sen: number) {
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR',
  }).format(sen / 100);
}

function rateLabel(contract: LocumContract) {
  return contract.rateBasis === 'hourly'
    ? 'locum.hourlyRate'
    : 'locum.perSessionRate';
}

export function ContractSummary({ contract }: { contract: LocumContract }) {
  const { t } = useTranslation();
  const active = contract.status === 'active';
  return (
    <div className={styles.stack}>
      <Badge
        tone={
          active
            ? 'success'
            : contract.status === 'cancelled'
              ? 'danger'
              : 'neutral'
        }
      >
        {t(`locum.status.${contract.status}`)}
      </Badge>
      <dl className={styles.summary}>
        <div>
          <dt>{t('locum.locum')}</dt>
          <dd>{contract.fullName}</dd>
        </div>
        <div>
          <dt>{t('locum.email')}</dt>
          <dd>{contract.email}</dd>
        </div>
        <div>
          <dt>{t('locum.branch')}</dt>
          <dd>{contract.branchName}</dd>
        </div>
        <div>
          <dt>{t('locum.contractPeriod')}</dt>
          <dd>
            {dateLabel(contract.startsOn)}–{dateLabel(contract.endsOn)}
          </dd>
        </div>
        <div>
          <dt>{t(rateLabel(contract))}</dt>
          <dd>{money(contract.rateSen)}</dd>
        </div>
        <div>
          <dt>{t('locum.accepted')}</dt>
          <dd>
            {contract.acceptedAt
              ? dateLabel(clinicDate(contract.acceptedAt))
              : t('locum.notAccepted')}
          </dd>
        </div>
      </dl>
      {contract.notes && (
        <div>
          <p className={styles.muted}>{t('locum.notes')}</p>
          <p>{contract.notes}</p>
        </div>
      )}
    </div>
  );
}

function OwnContractPage({ title }: { title: 'myContract' | 'myLocumPay' }) {
  const { t } = useTranslation();
  const contract = useMyLocumContract();
  const errorKind = locumContractErrorKind(contract.error);
  return (
    <PageContainer>
      <Card title={t(`locum.${title}`)}>
        {contract.isLoading ? (
          <LoadingState />
        ) : contract.error ? (
          <ErrorState
            error={contract.error}
            title={
              errorKind === 'not_found' ? t('locum.noContract') : undefined
            }
            body={
              errorKind === 'not_found'
                ? t('locum.errors.notFoundOwn')
                : errorKind === 'invalid_rate'
                  ? t('locum.errors.invalidRate')
                  : undefined
            }
            onRetry={() => void contract.refetch()}
          />
        ) : contract.data ? (
          <ContractSummary contract={contract.data} />
        ) : (
          <EmptyState
            title={t('locum.noContract')}
            body={t('locum.noContractBody')}
          />
        )}
      </Card>
    </PageContainer>
  );
}

export function MyContractPage() {
  return <OwnContractPage title="myContract" />;
}

export function MyLocumPayPage() {
  return <OwnContractPage title="myLocumPay" />;
}
